import { useEffect, useRef } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Problem from './components/Problem';
import Solution from './components/Solution';
import HowItWorks from './components/HowItWorks';
import Comparison from './components/Comparison';
import ROICalculator from './components/ROICalculator';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import B2BFooter from './components/B2BFooter';
import SEOSchema from '../../components/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goautomat.com';

function useSEO() {
  useEffect(() => {
    const prev = {
      title: document.title,
      desc: document.querySelector('meta[name="description"]')?.getAttribute('content') ?? '',
    };

    document.title =
      'Agente de IA para WhatsApp B2B | Automat — Resposta Instantânea para Seus Leads';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    const metas = [
      setMeta(
        'description',
        'Treine um agente de IA no seu catálogo e coloque ele pra atender seus clientes no WhatsApp. Qualificação automática, CRM integrado, 24/7. Demo gratuita.'
      ),
      setMeta('keywords', 'agente IA WhatsApp, automação vendas B2B, chatbot inteligente, qualificação leads'),
      setMeta('og:title', 'Agente de IA para WhatsApp B2B | Automat', true),
      setMeta(
        'og:description',
        'Treine um agente de IA no seu catálogo e coloque ele pra atender seus clientes no WhatsApp. Qualificação automática, CRM integrado, 24/7. Demo gratuita.',
        true
      ),
      setMeta('og:url', 'https://goautomat.com/b2b', true),
      setMeta('og:type', 'website', true),
      setMeta('og:image', `${siteUrl}/assets/media/og-cover-home.jpeg`, true),
      setMeta('twitter:card', 'summary_large_image'),
      setMeta('twitter:title', 'Agente de IA para WhatsApp B2B | Automat'),
      setMeta(
        'twitter:description',
        'Treine um agente de IA no seu catálogo e coloque ele pra atender seus clientes no WhatsApp. Demo gratuita.'
      ),
    ];

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://goautomat.com/b2b';

    return () => {
      document.title = prev.title;
      metas.forEach((m) => {
        try { document.head.removeChild(m); } catch { /* already removed */ }
      });
    };
  }, []);
}

export default function B2BPage() {
  useSEO();

  const formRef = useRef<HTMLDivElement>(null);
  const howItWorksRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
    };
  }, []);

  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const scrollToHowItWorks = () =>
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const b2bSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Agente de IA para WhatsApp B2B | Automat",
      "description": "Treine um agente de IA no seu catálogo e coloque ele pra atender seus clientes no WhatsApp. Qualificação automática, CRM integrado, 24/7. Demo gratuita.",
      "url": `${siteUrl}/b2b`,
      "inLanguage": "pt-BR",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Automat",
        "url": siteUrl
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Automat B2B — Agente de IA para WhatsApp",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "Agente de IA treinado no seu catálogo para atender clientes B2B no WhatsApp. Qualificação automática de leads, CRM integrado e resposta instantânea 24/7.",
      "url": `${siteUrl}/b2b`,
      "offers": {
        "@type": "Offer",
        "url": `${siteUrl}/b2b`,
        "availability": "https://schema.org/InStock",
        "priceCurrency": "BRL"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Automat",
        "url": siteUrl
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Como funciona o agente de IA da Automat para B2B?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Você treina o agente com seu catálogo de produtos, perguntas frequentes e scripts de vendas. O agente então atende automaticamente seus leads no WhatsApp, qualifica, agenda reuniões e integra com seu CRM, tudo em 24/7."
          }
        },
        {
          "@type": "Question",
          "name": "O agente de IA substitui minha equipe de vendas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Não substitui — potencializa. O agente cuida do primeiro contato, qualificação e perguntas frequentes, liberando sua equipe para focar em negociações de alto valor."
          }
        },
        {
          "@type": "Question",
          "name": "Qual o prazo de implementação do agente B2B?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A implementação completa com treinamento e integração é feita em até 7 dias úteis pela equipe especializada da Automat."
          }
        },
        {
          "@type": "Question",
          "name": "O agente funciona fora do horário comercial?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sim! O agente opera 24 horas por dia, 7 dias por semana, respondendo leads instantaneamente mesmo fora do horário comercial e nos fins de semana."
          }
        }
      ]
    }
  ];

  return (
    <div className="font-b2bbody">
      <SEOSchema schema={b2bSchemas} />
      <Header onCtaClick={scrollToForm} />

      <Hero onPrimaryCtaClick={scrollToForm} onSecondaryCtaClick={scrollToHowItWorks} />
      <Problem />
      <Solution />

      <section
        ref={howItWorksRef as React.RefObject<HTMLElement>}
        style={{ margin: 0, padding: 0 }}
      >
        <HowItWorks onCtaClick={scrollToForm} />
      </section>

      <Comparison />
      <ROICalculator onCtaClick={scrollToForm} />
      <FAQ />
      <FinalCTA formRef={formRef as React.RefObject<HTMLDivElement>} />
      <B2BFooter />
    </div>
  );
}
